/**
 * Admin Users Page
 * Manage all platform users — search, filter by role/status, and change roles.
 */

import { useEffect, useState, useCallback } from 'react';
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    TextField,
    InputAdornment,
    IconButton,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Stack,
    Chip,
    Alert,
    CircularProgress,
    Tooltip,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    TablePagination,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material';
import {
    Search as SearchIcon,
    Clear as ClearIcon,
    Refresh as RefreshIcon,
    AdminPanelSettings as MakeAdminIcon,
    PersonOff as RevokeAdminIcon,
} from '@mui/icons-material';
import { AuthenticatedLayout } from '../../components/layout/AuthenticatedLayout';
import { api } from '@shared/api/client';
import { useAuth } from '@shared/features/auth';

interface AdminUser {
    id: string;
    email: string;
    first_name?: string | null;
    last_name?: string | null;
    username?: string | null;
    status: string;
    is_active: boolean;
    role: string;
    category_id?: string | null;
    created_at: string;
}

function RoleChip({ role }: { role: string }) {
    const isAdmin = role === 'admin';
    return (
        <Chip
            label={isAdmin ? 'Admin' : 'User'}
            size="small"
            sx={{
                bgcolor: isAdmin ? 'rgba(139, 0, 0, 0.08)' : 'rgba(4, 55, 123, 0.08)',
                color: isAdmin ? '#8B0000' : '#04377b',
                fontWeight: 600,
                fontSize: '0.7rem',
                border: `1px solid ${isAdmin ? 'rgba(139,0,0,0.2)' : 'rgba(4,55,123,0.2)'}`,
            }}
        />
    );
}

export function AdminUsersPage() {
    const { firebase_uid } = useAuth();
    const [users, setUsers] = useState<AdminUser[]>([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Filters
    const [search, setSearch] = useState('');
    const [roleFilter, setRoleFilter] = useState('all');
    const [statusFilter, setStatusFilter] = useState('all');
    const [page, setPage] = useState(0);
    const [rowsPerPage] = useState(20);

    // Role change dialog
    const [roleDialogOpen, setRoleDialogOpen] = useState(false);
    const [targetUser, setTargetUser] = useState<AdminUser | null>(null);
    const [newRole, setNewRole] = useState<'admin' | 'user'>('user');
    const [roleChanging, setRoleChanging] = useState(false);

    const loadUsers = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const params: Record<string, any> = {
                page: page + 1,
                size: rowsPerPage,
            };
            if (search) params.email = search;
            if (roleFilter !== 'all') params.role = roleFilter;
            if (statusFilter === 'active') params.is_active = true;
            if (statusFilter === 'inactive') params.is_active = false;

            const res = await api.listUsers(params as any);
            const data = res.data?.data as any;
            setUsers(data?.users ?? []);
            setTotal(data?.total ?? 0);
        } catch (err) {
            setError('Failed to load users. Please try again.');
        } finally {
            setLoading(false);
        }
    }, [page, rowsPerPage, search, roleFilter, statusFilter]);

    useEffect(() => { loadUsers(); }, [loadUsers]);

    const handleRoleChangeClick = (user: AdminUser) => {
        setTargetUser(user);
        setNewRole(user.role === 'admin' ? 'user' : 'admin');
        setRoleDialogOpen(true);
    };

    const handleRoleChangeConfirm = async () => {
        if (!targetUser) return;
        try {
            setRoleChanging(true);
            await (api as any).changeUserRole(targetUser.id, { role: newRole });
            setRoleDialogOpen(false);
            setTargetUser(null);
            await loadUsers();
        } catch (err) {
            setError('Failed to update role. Please try again.');
        } finally {
            setRoleChanging(false);
        }
    };

    const getFullName = (user: AdminUser) => {
        const parts = [user.first_name, user.last_name].filter(Boolean);
        return parts.length > 0 ? parts.join(' ') : user.username || '—';
    };

    return (
        <AuthenticatedLayout>
            <Box>


                {error && (
                    <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                {/* Filters */}
                <Paper sx={{ p: 2, mb: 2 }} elevation={0} variant="outlined">
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="center">
                        <TextField
                            placeholder="Search by email..."
                            value={search}
                            onChange={(e) => { setSearch(e.target.value); setPage(0); }}
                            size="small"
                            sx={{ flexGrow: 1, maxWidth: 360 }}
                            InputProps={{
                                startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
                                endAdornment: search && (
                                    <InputAdornment position="end">
                                        <IconButton size="small" onClick={() => { setSearch(''); setPage(0); }}>
                                            <ClearIcon fontSize="small" />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <FormControl size="small" sx={{ minWidth: 130 }}>
                            <InputLabel>Role</InputLabel>
                            <Select
                                value={roleFilter}
                                label="Role"
                                onChange={(e: SelectChangeEvent) => { setRoleFilter(e.target.value); setPage(0); }}
                            >
                                <MenuItem value="all">All Roles</MenuItem>
                                <MenuItem value="user">User</MenuItem>
                                <MenuItem value="admin">Admin</MenuItem>
                            </Select>
                        </FormControl>
                        <FormControl size="small" sx={{ minWidth: 130 }}>
                            <InputLabel>Status</InputLabel>
                            <Select
                                value={statusFilter}
                                label="Status"
                                onChange={(e: SelectChangeEvent) => { setStatusFilter(e.target.value); setPage(0); }}
                            >
                                <MenuItem value="all">All Status</MenuItem>
                                <MenuItem value="active">Active</MenuItem>
                                <MenuItem value="inactive">Inactive</MenuItem>
                            </Select>
                        </FormControl>
                        <Tooltip title="Refresh">
                            <IconButton onClick={loadUsers} disabled={loading} color="primary" size="small"
                                sx={{ border: '1px solid', borderColor: 'primary.main', borderRadius: '50%', width: 32, height: 32 }}>
                                <RefreshIcon sx={{ fontSize: '1rem' }} />
                            </IconButton>
                        </Tooltip>
                    </Stack>
                </Paper>

                {/* Table */}
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                        <CircularProgress sx={{ color: '#04377b' }} />
                    </Box>
                ) : (
                    <TableContainer component={Paper} elevation={0} variant="outlined">
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ bgcolor: 'rgba(4, 55, 123, 0.04)' }}>
                                    {['Name', 'Email', 'Role', 'Status', 'Joined', 'Actions'].map((h) => (
                                        <TableCell key={h} sx={{ fontWeight: 600, color: '#04377b', fontSize: '0.75rem', py: 1 }}>
                                            {h}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {users.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                                            No users found matching your filters.
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    users.map((user) => (
                                        <TableRow
                                            key={user.id}
                                            sx={{
                                                '&:nth-of-type(odd)': { bgcolor: 'rgba(4, 55, 123, 0.015)' },
                                                '&:hover': { bgcolor: 'rgba(4, 55, 123, 0.03)' },
                                            }}
                                        >
                                            <TableCell sx={{ py: 1, fontSize: '0.8125rem' }}>
                                                {getFullName(user)}
                                            </TableCell>
                                            <TableCell sx={{ py: 1, fontSize: '0.75rem', fontFamily: 'monospace', color: 'text.secondary' }}>
                                                {user.email}
                                            </TableCell>
                                            <TableCell sx={{ py: 1 }}>
                                                <RoleChip role={user.role} />
                                            </TableCell>
                                            <TableCell sx={{ py: 1 }}>
                                                <Chip
                                                    label={user.is_active ? 'Active' : 'Inactive'}
                                                    size="small"
                                                    color={user.is_active ? 'success' : 'default'}
                                                    sx={{ fontSize: '0.7rem', height: 20 }}
                                                />
                                            </TableCell>
                                            <TableCell sx={{ py: 1, fontSize: '0.75rem', color: 'text.secondary' }}>
                                                {new Date(user.created_at).toLocaleDateString('en-IN', {
                                                    day: '2-digit', month: 'short', year: 'numeric',
                                                })}
                                            </TableCell>
                                            <TableCell sx={{ py: 1 }}>
                                                {/* Don't allow changing your own role */}
                                                {user.id !== firebase_uid && (
                                                    <Tooltip title={user.role === 'admin' ? 'Revoke Admin' : 'Make Admin'}>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleRoleChangeClick(user)}
                                                            sx={{
                                                                color: user.role === 'admin' ? '#8B0000' : '#04377b',
                                                                '&:hover': { bgcolor: user.role === 'admin' ? 'rgba(139,0,0,0.06)' : 'rgba(4,55,123,0.06)' },
                                                            }}
                                                        >
                                                            {user.role === 'admin'
                                                                ? <RevokeAdminIcon fontSize="small" />
                                                                : <MakeAdminIcon fontSize="small" />}
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                        <TablePagination
                            component="div"
                            count={total}
                            page={page}
                            onPageChange={(_, newPage) => setPage(newPage)}
                            rowsPerPage={rowsPerPage}
                            rowsPerPageOptions={[20]}
                        />
                    </TableContainer>
                )}
            </Box>

            {/* Role Change Confirmation Dialog */}
            <Dialog open={roleDialogOpen} onClose={() => setRoleDialogOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ color: newRole === 'admin' ? '#8B0000' : '#04377b' }}>
                    {newRole === 'admin' ? 'Grant Admin Access' : 'Revoke Admin Access'}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {newRole === 'admin'
                            ? `Are you sure you want to grant admin access to ${targetUser?.email}? They will be able to manage users and reference data.`
                            : `Are you sure you want to revoke admin access from ${targetUser?.email}? They will become a regular user.`}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRoleDialogOpen(false)} disabled={roleChanging}>Cancel</Button>
                    <Button
                        onClick={handleRoleChangeConfirm}
                        variant="contained"
                        disabled={roleChanging}
                        color={newRole === 'admin' ? 'error' : 'primary'}
                        startIcon={roleChanging ? <CircularProgress size={14} color="inherit" /> : undefined}
                    >
                        {roleChanging ? 'Updating...' : (newRole === 'admin' ? 'Grant Admin' : 'Revoke Admin')}
                    </Button>
                </DialogActions>
            </Dialog>
        </AuthenticatedLayout>
    );
}
